import './reducers';
import {
  applyMiddleware,
  combineReducers,
  createStore,
  compose,
  type Reducer,
  type StoreEnhancer,
  type Action,
} from 'redux';
import thunk, { type ThunkDispatch } from 'redux-thunk';
import {
  REMOVE_SESSION,
  cancelAllRequests,
  type EntitiesAction,
  type ItemsAction,
  type RequestAction,
  type SessionAction,
  type StreamAction,
} from './actions';
import { trigger } from './events';

import reducerRegistry, { type ReducersMap } from './util/reducerRegistry';
import type { EntitiesState } from './reducers/entities';
import type { ItemsState } from './reducers/items';
import type { RequestState } from './reducers/request';
import type { SessionState } from './reducers/session';
import type { StreamState } from './reducers/stream';
import { createSelector } from "reselect";

export interface RootState {
  entities: EntitiesState;
  items: ItemsState;
  request: RequestState;
  session: SessionState | null;
  stream: StreamState;
  [key: string]: unknown;
}

export type AppAction =
  | EntitiesAction
  | ItemsAction
  | RequestAction
  | SessionAction
  | StreamAction;

export type AppDispatch = ThunkDispatch<RootState, unknown, AppAction>;

export const createAppSelector = createSelector.withTypes<RootState>()

function configureStore(
  initialState: Record<string, any> = {},
  enhancers: any[] = [],
) {
  const combine = (reducers: ReducersMap): Reducer<any, Action> => {
    const newReducers: ReducersMap = { ...reducers };
    const reducerNames = Object.keys(newReducers);
    Object.keys(initialState).forEach((item) => {
      if (reducerNames.indexOf(item) === -1) {
        newReducers[item] = (state = null) => state;
      }
    });
    const appReducer = combineReducers(newReducers);
    return function rootReducer(state: any, action: Action) {
      let newState: any = { ...state };
      if (action.type === REMOVE_SESSION) {
        newState = undefined;
        cancelAllRequests();
        trigger('logout');
      }
      return appReducer(newState, action);
    };
  };

  const createEnhancer = (): StoreEnhancer => {
    enhancers.push(applyMiddleware(thunk));
    return compose(...enhancers);
  };

  const store = createStore(
    combine(reducerRegistry.getReducers()),
    initialState,
    createEnhancer(),
  );

  // Replace the store's reducer whenever a new reducer is registered.
  reducerRegistry.setChangeListener((reducers) => {
    store.replaceReducer(combine(reducers));
  });

  return store;
}

export default configureStore;
